import {
  FeatureFlagKey,
  getFeatureFlagMeta,
  type FeatureFlagMap,
} from "encamp-shared/src/featureFlags";

import type { GroupChildNavItem, StandardNavItem } from "../../components/LeftNavV2/NavItemV2";

import { type PreviewVariant } from "../../components/PreviewFeature/constants";

/**
 * Gating shared by every nav-item builder.
 *
 * `visibilityFlag` and `previewFlag` both point at a feature flag, but they answer
 * different questions:
 *
 * - **`visibilityFlag`** controls whether the entry exists at all — the item is
 *   hidden unless the flag resolves on. Reach for it when the whole experience is
 *   gated. When that flag is also `inDevelopment`, it doubles as the preview source,
 *   so a gated in-development entry needs nothing else.
 * - **`previewFlag`** only drives the in-development preview icon, without gating
 *   visibility. Reach for it when there's an existing always-visible experience and
 *   you want to layer a preview marker on top of it (e.g. the v1 Manifests page that
 *   previews its not-yet-enabled `ManifestViewsV2` surface).
 *
 * In short: gate-then-preview → `visibilityFlag`; preview-on-top-of-an-existing-page
 * → `previewFlag`. The preview source resolves to `previewFlag ?? visibilityFlag`.
 */
type NavItemGating = {
  /** Hide the item unless this flag is enabled; also drives the preview icon when inDevelopment. */
  visibilityFlag?: FeatureFlagKey;
  /** Extra visibility condition beyond `visibilityFlag` (e.g. permissions). Defaults to true. */
  visible?: boolean;
  /**
   * Flag whose inDevelopment state drives the preview icon for an un-gated entry.
   * Defaults to `visibilityFlag`.
   */
  previewFlag?: FeatureFlagKey;
};

function isVisible(
  flags: Partial<FeatureFlagMap> | undefined,
  visibilityFlag: FeatureFlagKey | undefined,
  visible: boolean
): boolean {
  return visible && (visibilityFlag === undefined || Boolean(flags?.[visibilityFlag]));
}

/** The preview affordance only applies to flags marked inDevelopment in metadata. */
function previewFlagForNavGate(flag: FeatureFlagKey | undefined): FeatureFlagKey | undefined {
  return flag && getFeatureFlagMeta(flag).inDevelopment ? flag : undefined;
}

/**
 * Resolves the preview-icon fields. An explicit `previewFlag` means the entry has an
 * existing experience with a new version on top (`newVersion`); deriving from the
 * `visibilityFlag` gate means the whole entry is the in-development experience (`gated`).
 */
function previewFields(
  visibilityFlag: FeatureFlagKey | undefined,
  previewFlag: FeatureFlagKey | undefined
): { previewFlag?: FeatureFlagKey; previewVariant?: PreviewVariant } {
  const resolvedPreviewFlag = previewFlagForNavGate(previewFlag ?? visibilityFlag);
  if (!resolvedPreviewFlag) {
    return {};
  }

  return {
    previewFlag: resolvedPreviewFlag,
    previewVariant: previewFlag !== undefined ? "newVersion" : "gated",
  };
}

export type GroupChildNavItemConfig = NavItemGating & {
  text: string;
  path: string;
};

export function createGroupChildNavItem(
  flags: Partial<FeatureFlagMap> | undefined,
  { text, path, visibilityFlag, visible = true, previewFlag }: GroupChildNavItemConfig
): GroupChildNavItem | null {
  if (!isVisible(flags, visibilityFlag, visible)) {
    return null;
  }

  return {
    kind: "GroupChild",
    text,
    path,
    ...previewFields(visibilityFlag, previewFlag),
  };
}

export type StandardNavItemConfig = NavItemGating &
  Omit<StandardNavItem, "kind" | "previewFlag" | "previewVariant">;

export function createStandardNavItem(
  flags: Partial<FeatureFlagMap> | undefined,
  { visibilityFlag, visible = true, previewFlag, ...item }: StandardNavItemConfig
): StandardNavItem | null {
  if (!isVisible(flags, visibilityFlag, visible)) {
    return null;
  }

  return {
    kind: "Standard",
    ...item,
    ...previewFields(visibilityFlag, previewFlag),
  };
}
